/**
 * Sourcing provider boundary (Phase 9B, spec §26A) — pure TypeScript,
 * retailer-neutral. Berrier Reno must never be architecturally dependent
 * on one retailer: Home Depot, Lowe's, Floor & Decor, Ferguson, local
 * suppliers, wholesalers, distributor feeds and manual contractor quotes
 * all fit the same Offer model. A provider DECLARES its capabilities —
 * nothing pretends every provider supports everything, and nothing here
 * fabricates live data.
 *
 * Live data honesty (permanent): an offer is provider data ONLY when a
 * real provider import stamped its provider_id. Manually recorded and
 * historical observations are always labeled manual/historical — never
 * "live" or "current provider data".
 *
 * SaaS note (spec §10): future live integrations run server-side behind
 * this same interface; the desktop app ships descriptors + outbound URL
 * building only, which is browser-safe.
 */

export interface ProviderCapabilities {
  /** Can search the provider's product catalog (live). */
  productSearch: boolean;
  /** Can fetch product details (live). */
  productDetails: boolean;
  /** Can fetch current pricing (live). */
  livePricing: boolean;
  /** Can scope price/availability to a store/market (live). */
  storePricing: boolean;
  /** Can report stock availability (live). */
  availability: boolean;
  /** Can build an outbound product/search URL (no live data needed). */
  outboundUrls: boolean;
}

export interface SourcingProvider {
  id: string;
  displayName: string;
  capabilities: ProviderCapabilities;
  /** Why live capabilities are unavailable, when they are. Shown verbatim. */
  liveDataNote: string | null;
  /**
   * Best outbound product page URL for an offer/product. Prefers an
   * explicit stored URL; may fall back to a deterministic search deep link
   * from a SKU/model. Returns null when nothing trustworthy can be built.
   */
  productPageUrl(input: { url?: string | null; sku?: string | null }): string | null;
  /** Outbound search URL for a free-text query, when supported. */
  searchUrl(query: string): string | null;
}

const genericUrlOnly = (input: { url?: string | null }): string | null => {
  const url = (input.url ?? "").trim();
  return /^https?:\/\//i.test(url) ? url : null;
};

/** Manually recorded observations (the default provenance). */
export const MANUAL_PROVIDER: SourcingProvider = {
  id: "manual",
  displayName: "Manual / Recorded",
  capabilities: {
    productSearch: false,
    productDetails: false,
    livePricing: false,
    storePricing: false,
    availability: false,
    outboundUrls: true,
  },
  liveDataNote: null,
  productPageUrl: genericUrlOnly,
  searchUrl: () => null,
};

/**
 * Home Depot — the first provider target (spec §26A), deliberately limited
 * to outbound URLs today. Audit result (2026-08): Home Depot offers NO
 * official public developer API for product search, pricing, or store
 * availability. Live data would require a licensed third-party data
 * service (e.g. commercial product-data APIs) or an official partnership;
 * either plugs in behind this interface (server-side under SaaS) without
 * touching the domain. Scraping homedepot.com or automating its site is
 * explicitly out of scope. Until then: offers are recorded manually and
 * honestly labeled as such.
 */
export const HOME_DEPOT_PROVIDER: SourcingProvider = {
  id: "home-depot",
  displayName: "Home Depot",
  capabilities: {
    productSearch: false,
    productDetails: false,
    livePricing: false,
    storePricing: false,
    availability: false,
    outboundUrls: true,
  },
  liveDataNote:
    "Home Depot has no official public developer API (verified Aug 2026). Prices and availability are recorded manually from the store or website; a licensed data feed can plug in behind this provider boundary later without changing the app.",
  productPageUrl(input) {
    const direct = genericUrlOnly(input);
    if (direct) return direct;
    const sku = (input.sku ?? "").trim();
    // A search deep link by SKU/model is deterministic and robust; guessing
    // /p/ product paths is not.
    return sku !== "" ? `https://www.homedepot.com/s/${encodeURIComponent(sku)}` : null;
  },
  searchUrl(query) {
    const q = query.trim();
    return q !== "" ? `https://www.homedepot.com/s/${encodeURIComponent(q)}` : null;
  },
};

export const SOURCING_PROVIDERS: ReadonlyArray<SourcingProvider> = [MANUAL_PROVIDER, HOME_DEPOT_PROVIDER];

export function providerById(id: string | null): SourcingProvider {
  return SOURCING_PROVIDERS.find((p) => p.id === id) ?? MANUAL_PROVIDER;
}

/** Provenance label for an offer: provider display name or Manual. Never
 *  labels manual/historical data as live. */
export function offerProvenanceLabel(providerId: string | null): string {
  return providerId === null ? "Manual" : providerById(providerId).displayName;
}
